import React, { useEffect, useState } from "react";
import {
  IconMinimize,
  IconPlus,
  IconDownload,
  IconEdit,
  IconTrash,
  IconCheck,
  IconPencil,
  IconX,
  IconDeviceFloppy,
  IconFileImport,
} from "@tabler/icons-react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleFullScreen,
  toggleFinalizationStatus,
  setActiveApplicationTab,
} from "../../state/reducers/applicationSlice";
import {
  addFinalizedlistingEntry,
  addShortlistingEntry,
  finalizeUniversity,
  getPostFinalizedData,
  postFinalizedData,
  sendFinalizedNotification,
  sendShortlistingNotification,
} from "../../services/admin.services";
import * as XLSX from "xlsx";
import { updateCustomer } from "../../services/customer.services";
import EditableNotes from "./EditableNotes";
import { ImportData } from "../../services/application.services";
import { putFinalizedData } from "../../services/postService.services";
import toast from "react-hot-toast";
import { getApplicationRecords } from "../../services/application.services";

const isTagKey = (k) => {
  const n = normalizeHeaderName(k);
  return n === "tag" || n === "tags";
};

// utils/normalize.js (optional file — or inline these helpers)
const normalizeKey = (k) =>
  String(k)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

const normalizeRowKeys = (row) => {
  const out = {};
  Object.keys(row || {}).forEach((k) => {
    out[normalizeKey(k)] = row[k];
  });
  return out;
};

const buildRecordForBackend = (
  rawRow,
  selectedStudent,
  assignedApplicationManagerId
) => {
  const row = normalizeRowKeys(rawRow);
  console.log(assignedApplicationManagerId);
  // --- NEW: alias mapping ---
  const course_name = pick(row, ["course_name", "program_name"]); // program_name -> course_name
  const university = pick(row, [
    "university",
    "university_name",
    "univ",
    "uni",
    "college",
    "institute",
  ]);
  const intake = row.intake ?? ""; // keep; backend parses flexibly

  const record = {
    name: row.name ?? (selectedStudent?.username || ""),
    intake,
    country: row.country ?? "",
    university, // <- now filled from aliases
    course_name,
    status: row.status ?? "Pending",
    assigned_aplication_manager: assignedApplicationManagerId ?? null,
  };

  // Everything else → dynamic_fields (exclude fixed + known alias sources)
  const aliasKeys = new Set([
    "program_name",
    "university_name",
    "univ",
    "uni",
    "college",
    "institute",
  ]);

  const dynamic_fields = {};
  for (const [k, v] of Object.entries(row)) {
    if (FIXED_FIELDS.has(k)) continue;
    if (aliasKeys.has(k)) continue;
    dynamic_fields[k] = v;
  }

  return { ...record, dynamic_fields };
};

// --- In your component file ---
const FIXED_FIELDS = new Set([
  "name",
  "intake",
  "country",
  "university",
  "course_name",
  "assigned_aplication_manager",
  "status",
]);

const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return "";
};

const MONTHS_MAP = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

const MONTHS_OUTPUT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const recoverExcelTimeDate = (d) => {
  if (!d || isNaN(d.getTime())) return d;
  const yr = d.getUTCFullYear();
  if (yr <= 1900) {
    const m = d.getUTCMonth();
    const dayOfMonth = d.getUTCDate();
    const daysBeforeMonth = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
    const totalDays = (daysBeforeMonth[m] || 0) + dayOfMonth;
    const hours = d.getUTCHours();
    const recYear = totalDays * 24 + hours;
    const recMonth = d.getUTCMinutes();
    const recDay = d.getUTCSeconds();
    if (recYear >= 2020 && recYear <= 2040 && recMonth >= 1 && recMonth <= 12 && recDay >= 1 && recDay <= 31) {
      return new Date(recYear, recMonth - 1, recDay);
    }
  }
  return d;
};

// Robust date parser that handles human strings, ordinals, prefixes, ISO, and Excel numbers
const parseFlexibleDate = (input) => {
  if (!input) return null;
  if (input instanceof Date && !isNaN(input.getTime())) return recoverExcelTimeDate(input);

  let str = String(input).trim();
  if (!str) return null;

  // Ignore non-date strings
  if (/^(rolling|tbd|n\/?a|na|tentative|none|asap)$/i.test(str)) {
    return null;
  }

  // Recover dates corrupted by Excel duration/time interpretation: e.g. "24-03-1900 9.10.23 AM" or "1900-03-24..."
  if (str.includes("1900")) {
    const match1900 = str.match(/(\d{1,2})[-\/. ](\d{1,2})[-\/. ]1900[\s,]+(\d{1,2})[:.](\d{1,2})[:.](\d{1,2})/i) ||
                      str.match(/1900[-\/. ](\d{1,2})[-\/. ](\d{1,2})[T\s,]+(\d{1,2})[:.](\d{1,2})[:.](\d{1,2})/i);
    if (match1900) {
      let dVal, mVal, hr, min, sec;
      if (str.includes("1900-") || str.startsWith("1900")) {
        mVal = parseInt(match1900[1], 10);
        dVal = parseInt(match1900[2], 10);
      } else {
        dVal = parseInt(match1900[1], 10);
        mVal = parseInt(match1900[2], 10);
      }
      hr = parseInt(match1900[3], 10);
      min = parseInt(match1900[4], 10);
      sec = parseInt(match1900[5], 10);
      const daysBefore = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
      const totDays = (daysBefore[mVal - 1] || 0) + dVal;
      const recY = totDays * 24 + hr;
      const recM = min;
      const recD = sec;
      if (recY >= 2020 && recY <= 2040 && recM >= 1 && recM <= 12 && recD >= 1 && recD <= 31) {
        return new Date(recY, recM - 1, recD);
      }
    }
  }

  // 1. Excel serial date number (e.g. 45678, 46387)
  const num = Number(str);
  if (!isNaN(num) && num > 30000 && num < 70000) {
    const epoch = new Date(Math.round((num - 25569) * 86400 * 1000));
    return new Date(epoch.getUTCFullYear(), epoch.getUTCMonth(), epoch.getUTCDate());
  }

  // Clean up ordinal suffixes (15th -> 15, 1st -> 1, etc.)
  const cleaned = str.replace(/(\d{1,2})(st|nd|rd|th)\b/gi, "$1");

  // 2. ISO yyyy-mm-dd or yyyy/mm/dd or yyyy.mm.dd (with optional timestamp)
  const isoMatch = cleaned.match(/\b(\d{4})[-\/\. ](\d{1,2})[-\/\. ](\d{1,2})/);
  if (isoMatch) {
    const y = parseInt(isoMatch[1], 10);
    const m = parseInt(isoMatch[2], 10) - 1;
    const d = parseInt(isoMatch[3], 10);
    return new Date(y, m, d);
  }

  // 3. Named month day-first: "15 Jan 2027", "15-Jan-2027", "15/Jan/2027", "15.Jan.2027", "15th January 2027"
  const dayFirstMatch = cleaned.match(/\b(\d{1,2})[\s\-_\.\/]+([A-Za-z]{3,9})[\s\-_\.\/,]+(\d{2,4})\b/);
  if (dayFirstMatch) {
    const day = parseInt(dayFirstMatch[1], 10);
    const monStr = dayFirstMatch[2].toLowerCase();
    let year = parseInt(dayFirstMatch[3], 10);
    if (year < 100) year += 2000;
    if (MONTHS_MAP[monStr] !== undefined) {
      return new Date(year, MONTHS_MAP[monStr], day);
    }
  }

  // 4. Named month month-first: "Jan 15, 2027", "January 15th, 2027", "Jan-15-2027"
  const monFirstMatch = cleaned.match(/\b([A-Za-z]{3,9})[\s\-_\.\/]+(\d{1,2})[\s\-_\.\/,]+(\d{2,4})\b/);
  if (monFirstMatch) {
    const monStr = monFirstMatch[1].toLowerCase();
    const day = parseInt(monFirstMatch[2], 10);
    let year = parseInt(monFirstMatch[3], 10);
    if (year < 100) year += 2000;
    if (MONTHS_MAP[monStr] !== undefined) {
      return new Date(year, MONTHS_MAP[monStr], day);
    }
  }

  // 5. Numeric: dd/mm/yyyy or dd-mm-yyyy or dd.mm.yyyy or mm/dd/yyyy
  const numericMatch = cleaned.match(/\b(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})\b/);
  if (numericMatch) {
    const p1 = parseInt(numericMatch[1], 10);
    const p2 = parseInt(numericMatch[2], 10);
    let y = parseInt(numericMatch[3], 10);
    if (y < 100) y += 2000;

    if (p1 > 12) {
      return new Date(y, p2 - 1, p1);
    } else if (p2 > 12) {
      return new Date(y, p1 - 1, p2);
    } else {
      return new Date(y, p2 - 1, p1); // default dd/mm/yyyy
    }
  }

  // 6. Month + Year only (e.g. "January 2027" or "Jan 2027") -> defaults to 1st of month
  const monthYearMatch = cleaned.match(/\b([A-Za-z]{3,9})[\s\-_\.\/,]+(\d{4})\b/);
  if (monthYearMatch && MONTHS_MAP[monthYearMatch[1].toLowerCase()] !== undefined) {
    return new Date(parseInt(monthYearMatch[2], 10), MONTHS_MAP[monthYearMatch[1].toLowerCase()], 1);
  }

  // 7. Day + Month only without year (e.g. "15 Jan", "15th January") -> current or next year
  const dayMonthMatch = cleaned.match(/\b(\d{1,2})[\s\-_\.\/]+([A-Za-z]{3,9})\b/);
  if (dayMonthMatch && MONTHS_MAP[dayMonthMatch[2].toLowerCase()] !== undefined) {
    const now = new Date();
    let yr = now.getFullYear();
    const targetMonth = MONTHS_MAP[dayMonthMatch[2].toLowerCase()];
    const targetDay = parseInt(dayMonthMatch[1], 10);
    const candidate = new Date(yr, targetMonth, targetDay);
    if (candidate < now) yr += 1;
    return new Date(yr, targetMonth, targetDay);
  }

  // 8. Generic JS Date parse fallback
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    return recoverExcelTimeDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  }

  return null;
};

const isYMD = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

const toYMDLocal = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Formats strictly as "15 Jan 2027" (DD Mon YYYY)
const formatDisplayDate = (value) => {
  if (!value) return "";
  try {
    const d = parseFlexibleDate(value);
    if (!d || isNaN(d.getTime())) return String(value);

    const day = String(d.getDate()).padStart(2, "0");
    const month = MONTHS_OUTPUT[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return String(value);
  }
};

// For <input type="date"> binding
const getFormattedDateForInput = (value) => {
  if (!value) return "";
  if (isYMD(value)) return value;
  const d = parseFlexibleDate(value);
  return d ? toYMDLocal(d) : "";
};

// For saving to backend in clean "15 Jan 2027" format
const formatBackendDate = (value) => {
  if (!value) return "";
  return formatDisplayDate(value);
};

const isValidUrl = (value) => {
  return (
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"))
  );
};

// Utility function to normalize header names (lowercase + trim)
const normalizeHeaderName = (header) => {
  if (typeof header === "string") return header.toLowerCase().trim();
  if (header == null) return ""; // map null/undefined to empty header
  return String(header).toLowerCase().trim();
};

// Utility function to remove duplicate columns based on normalized names
const removeDuplicateColumns = (columns) => {
  const seen = new Set();
  return columns.filter((col) => {
    const normalizedName = normalizeHeaderName(col.name);
    if (seen.has(normalizedName)) {
      return false;
    }
    seen.add(normalizedName);
    return true;
  });
};

// Function to calculate optimal column width based on content
const calculateColumnWidth = (
  columnData,
  headerText,
  minWidth = 120,
  maxWidth = 400
) => {
  // Calculate header width
  const headerWidth = headerText.length * 8 + 40; // Approximate character width + padding

  // Calculate content width by sampling data
  const contentWidths = columnData
    .slice(0, Math.min(50, columnData.length)) // Sample first 50 rows for performance
    .map((value) => {
      if (!value) return 0;
      const strValue = String(value);

      // Handle URLs differently (they can be longer but should wrap)
      if (strValue.startsWith("http")) {
        return Math.min(strValue.length * 6, 250);
      }

      // For regular text, estimate pixel width
      return strValue.length * 8;
    });

  const maxContentWidth = Math.max(...contentWidths, 0);
  const optimalWidth = Math.max(headerWidth, maxContentWidth) + 20; // Add padding

  // Ensure within bounds
  return Math.max(minWidth, Math.min(maxWidth, optimalWidth));
};

// Function to get column type and set appropriate width constraints
const getColumnConstraints = (columnName) => {
  const normalizedName = normalizeHeaderName(columnName);

  // Define width constraints based on column type
  const constraints = {
    "sr.no.": { min: 80, max: 100, fixed: true },
    status: { min: 120, max: 180, fixed: false },
    tag: { min: 100, max: 150, fixed: false },
    actions: { min: 100, max: 120, fixed: true },
  };

  // Check for specific column types
  if (normalizedName.includes("link") || normalizedName.includes("url")) {
    return { min: 200, max: 350, fixed: false };
  }

  if ((normalizedName.includes("deadline") || normalizedName.includes("date")) && !normalizedName.includes("note")) {
    return { min: 140, max: 180, fixed: false };
  }

  if (
    ["remarks", "details", "requirements", "notes"].some((keyword) =>
      normalizedName.includes(keyword)
    )
  ) {
    return { min: 200, max: 400, fixed: false };
  }

  if (
    normalizedName.includes("university") ||
    normalizedName.includes("college")
  ) {
    return { min: 180, max: 300, fixed: false };
  }

  // Return specific constraints or default
  return constraints[normalizedName] || { min: 120, max: 300, fixed: false };
};

const UniversityTable = ({ activeTab, fetchStudentDetails }) => {
  const dispatch = useDispatch();
  const admins = useSelector((state) => state.admin.admins);
  const selectedStudent = useSelector((state) => state.app.selectedStudent);
  const isTableFullScreen = useSelector(
    (state) => state.application.isTableFullScreen
  );
  const [columnOrder, setColumnOrder] = useState([]);
  const [note, setNote] = useState("");
  // State management
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [data, setData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [editableColumns, setEditableColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isPosting, setIsPosting] = useState(false);
  const [selectedManager, setSelectedManager] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusOptions] = useState([
    "Submitted",
    "Approved",
    "Rejected",
    "Pending",
    "Finalized",
  ]);
  const [tagOptions] = useState(["Ambitious", "Moderate", "Safe"]);
  const [columnToDelete, setColumnToDelete] = useState(null);
  const [isDeleteColumnModalOpen, setIsDeleteColumnModalOpen] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);
  const studentId = selectedStudent?.student_id;
  const assignedApplicationManagerId =
    selectedStudent.assigned_admins?.assigned_application_manager?.id || null;
  const [applicationData, setApplicationData] = useState([]);
  const currentApplicationTab = useSelector(
    (state) => state.application.activeApplicationTab || "finalizedUniversity"
  );

  // Helper functions
  const generateUniqueId = () =>
    Date.now() * 1000 + Math.floor(Math.random() * 1000);

  const getFormattedDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const parsedDate = new Date(dateString);
      return isNaN(parsedDate) ? "" : formatBackendDate(parsedDate);
    } catch {
      return "";
    }
  };

  const handleChange = (e) => {
    console.log("Note changed:", e.target.value);
    setNote(e.target.value);
  };

  const ensureStatusField = (rows) => {
    return rows.map((row) => {
      if (
        !row.hasOwnProperty("status") ||
        row.status === undefined ||
        row.status === null
      ) {
        return { ...row, status: "Pending" };
      }
      return row;
    });
  };

  const handleFetchStudentDetails = () => {
    // First call the original function
    fetchStudentDetails();

    // Then ensure we restore the active tab
    setTimeout(() => {
      dispatch(setActiveApplicationTab(currentApplicationTab));
    }, 50);
  };

  // Enhanced function to build dynamic columns
  const buildDynamicColumns = (data, orderedHeaders, deletedColumns = []) => {
    // Filter out metadata rows for width calculation
    const cleanData = data.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    const renderTextWithLinks = (text) => {
      if (typeof text !== "string") return text;

      let cleanedText = text
        .replace(/https?:\/\/https?:\/\//g, "https://")
        .replace(/(^|\s)tps:\/\//g, "$1https://");

      const urlRegex = /https?:\/\/[^\s]+/g;
      const matchedUrls = [...cleanedText.matchAll(urlRegex)].map((m) => m[0]);
      const uniqueUrls = [...new Set(matchedUrls)];

      let finalParts = [cleanedText];
      uniqueUrls.forEach((url) => {
        finalParts = finalParts.flatMap((part, i) =>
          typeof part === "string" && part.includes(url)
            ? part.split(url).flatMap((seg, j, arr) =>
                j < arr.length - 1
                  ? [
                      seg,
                      <a
                        key={`${url}-${i}-${j}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                      >
                        {url}
                      </a>,
                    ]
                  : [seg]
              )
            : [part]
        );
      });

      // NEW: preserve newlines
      const withLineBreaks = finalParts.flatMap((part, idx) => {
        if (typeof part !== "string") return [part];
        const pieces = part.split(/\r?\n/);
        return pieces.flatMap((p, i) =>
          i < pieces.length - 1 ? [p, <br key={`br-${idx}-${i}`} />] : [p]
        );
      });

      // return <>{finalParts}</>;
      return <>{withLineBreaks}</>;
    };

    // Fixed columns with specific widths
    const fixedColumns = [
      {
        name: "Sr.No.",
        cell: (row, index) => index + 1,
        center: true,
        width: "80px",
        grow: 0,
      },
    ];

    // Status column with dynamic width
    const statusColumn = orderedHeaders.includes("status")
      ? [
          {
            name: "status",
            selector: (row) => row.status || "",
            center: true,
            width: `${calculateColumnWidth(
              cleanData.map((row) => row.status),
              "Status",
              120,
              180
            )}px`,
            grow: 0,
            cell: (row) => (
              <span
                className={`px-2 py-1 rounded-full text-white text-sm whitespace-nowrap ${
                  row.status === "Submitted"
                    ? "bg-blue-500"
                    : row.status === "Approved"
                    ? "bg-green-500"
                    : row.status === "Rejected"
                    ? "bg-red-500"
                    : row.status === "Pending"
                    ? "bg-orange-500 text-black"
                    : row.status === "Finalized"
                    ? "bg-purple-500"
                    : "bg-gray-400"
                }`}
              >
                {row.status}
              </span>
            ),
          },
        ]
      : [];

    // Tag column if exists
    const tagKey = orderedHeaders.find((h) => isTagKey(h));
    const tagColumn = tagKey
      ? [
          {
            name: tagKey, // keep the same header name the user uploaded
            selector: (row) => row[normalizeHeaderName(tagKey)] || "",
            center: true,
            width: `${calculateColumnWidth(
              cleanData.map((row) => row[normalizeHeaderName(tagKey)]),
              tagKey,
              100,
              150
            )}px`,
            grow: 0,
            cell: (row) => (
              <div className="text-center">
                {renderTextWithLinks(row[normalizeHeaderName(tagKey)] || "")}
              </div>
            ),
          },
        ]
      : [];

    // Dynamic columns for remaining headers
    const excludedColumns = ["tag", "tags", "status", "sr.no.", "id"];
    const dynamicColumns = orderedHeaders
      .filter((key) => !excludedColumns.includes(key))
      .filter(
        (key) =>
          !deletedColumns.map((col) => normalizeHeaderName(col)).includes(key)
      )
      .map((key) => {
        const nKey = normalizeHeaderName(key);
        const columnData = cleanData.map((row) => row[nKey]);
        const constraints = getColumnConstraints(nKey);
        const width = constraints.fixed
          ? constraints.min
          : calculateColumnWidth(
              columnData,
              key,
              constraints.min,
              constraints.max
            );

        return {
          name: key, // keep original header text for display
          // selector: (row) => row[nKey] || "",
          selector: (row) =>
            row[nKey] ?? row[key] ?? row[normalizeKey(key)] ?? "",
          center: true,
          width: `${width}px`,
          minWidth: `${constraints.min}px`,
          maxWidth: `${constraints.max}px`,
          grow: constraints.fixed ? 0 : 1,
          // cell: (row) => (
          //   <div
          //     className="text-left p-2 break-words"
          //     style={{
          //       wordWrap: "break-word",
          //       overflowWrap: "anywhere",
          //       whiteSpace: "normal",
          //     }}
          //   >
          //     {renderTextWithLinks(row[nKey] || "")}
          //   </div>
          // ),
          cell: (row) => {
            // robust value fetch
            let value = row[nKey] ?? row[key] ?? row[normalizeKey(key)] ?? "";

            // pretty print dates in view mode
            const isDateKey =
              (nKey.includes("deadline") || nKey.includes("date")) && !nKey.includes("note");
            if (isDateKey) {
              value = formatDisplayDate(
                typeof value === "string" ? value : String(value ?? "")
              );
            }

            // always string-coerce for the linkifier
            const safeText =
              typeof value === "string" ? value : String(value ?? "");

            return (
              <div
                className="text-left p-2 break-words"
                style={{
                  // wordWrap: "break-word",
                  // overflowWrap: "anywhere",
                  // whiteSpace: "normal",
                  wordWrap: "break-word",
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap", // preserve \n and multiple spaces
                }}
              >
                {renderTextWithLinks(safeText)}
              </div>
            );
          },
        };
      });

    return [...fixedColumns, ...statusColumn, ...tagColumn, ...dynamicColumns];
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    if (isEditMode) {
      // Save all changes when exiting edit mode
      handleSaveAllChanges();
    } else {
      // Enter edit mode
      console.log("Entering edit mode, current data:", data);

      // Create deep clone of data - filter out metadata rows
      const cleanData = data.filter(
        (row) => !row.__columnOrder && !row.__deletedColumns
      );
      console.log("Clean data for editing:", cleanData);

      setEditData(JSON.parse(JSON.stringify(cleanData)));

      // Set up editable columns immediately after setting edit data
      setTimeout(() => {
        prepareEditableColumns();
      }, 0);
    }
    setIsEditMode(!isEditMode);
  };

  const prepareEditableColumns = () => {
    console.log("Preparing editable columns...");
    console.log("Current data:", data);
    console.log("Current editData:", editData);

    // Use the current data state to determine columns, not editData
    const workingData = data.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    if (workingData.length === 0) {
      console.warn("No valid data rows found for preparing columns");
      // If no data, create basic columns
      setEditableColumns([
        {
          name: "status",
          originalName: "status",
          isEditing: false,
        },
      ]);
      return;
    }

    // Get all unique column names from all rows
    const allColumnNames = new Set();
    workingData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (
          ![
            "id",
            "sr.no.",
            "srno",
            "sr. no.",
            "edit",
            "delete",
            "actions",
          ].includes(normalizeHeaderName(key))
        ) {
          allColumnNames.add(key);
        }
      });
    });

    console.log("All column names found:", Array.from(allColumnNames));

    // Get deleted columns to exclude them
    const deletedColumnRow = data.find((row) => row.__deletedColumns);
    const deletedColumns = deletedColumnRow?.__deletedColumns || [];

    const editable = columnOrder
      .filter((key) => {
        // Exclude deleted columns
        return !deletedColumns.some(
          (deletedCol) =>
            normalizeHeaderName(deletedCol) === normalizeHeaderName(key)
        );
      })
      .map((key) => ({
        name: normalizeHeaderName(key),
        originalName: key,
        isEditing: false,
      }));

    // Remove duplicates based on normalized names
    const uniqueEditable = [];
    const seen = new Set();

    editable.forEach((col) => {
      const normalizedName = normalizeHeaderName(col.name);
      if (!seen.has(normalizedName)) {
        seen.add(normalizedName);
        uniqueEditable.push(col);
      }
    });

    // Ensure status is first if it exists
    const statusIndex = uniqueEditable.findIndex(
      (col) => normalizeHeaderName(col.name) === "status"
    );
    if (statusIndex > 0) {
      const statusCol = uniqueEditable.splice(statusIndex, 1)[0];
      uniqueEditable.unshift(statusCol);
    }

    console.log("Final editable columns:", uniqueEditable);
    setEditableColumns(uniqueEditable);
  };

  // Handle cell value changes
  const handleCellChange = (rowIndex, columnName, value) => {
    console.log("Cell change:", { rowIndex, columnName, value }); // Debug log

    const updatedData = [...editData];
    if (updatedData[rowIndex]) {
      // Handle both normalized and original column names
      const normalizedColumnName = normalizeHeaderName(columnName);
      updatedData[rowIndex][normalizedColumnName] = value;

      // Also update the original column name if it's different
      if (columnName !== normalizedColumnName) {
        updatedData[rowIndex][columnName] = value;
      }

      console.log("Updated row:", updatedData[rowIndex]); // Debug log
      setEditData(updatedData);
    } else {
      console.error("Row not found at index:", rowIndex);
    }
  };

  // Handle column name changes
  const handleColumnNameChange = (index, value) => {
    const normalizedValue = normalizeHeaderName(value);

    // Check for duplicates
    const existingNames = editableColumns
      .filter((_, i) => i !== index)
      .map((col) => normalizeHeaderName(col.name));

    if (existingNames.includes(normalizedValue)) {
      toast.error(
        "Column name already exists. Please choose a different name."
      );
      return;
    }

    const updatedColumns = [...editableColumns];
    updatedColumns[index].name = value.toLowerCase().trim(); // Store as lowercase
    setEditableColumns(updatedColumns);
  };

  // Start editing column name
  const toggleColumnNameEditing = (index) => {
    const updatedColumns = [...editableColumns];
    updatedColumns[index].isEditing = !updatedColumns[index].isEditing;
    setEditableColumns(updatedColumns);
  };

  // Enhanced function for editable columns with dynamic widths
  const getEditableDataColumnsWithDynamicWidth = (
    editData,
    editableColumns
  ) => {
    if (!editData || !editableColumns) return [];

    const editableDataColumns = [
      {
        name: "Sr.No.",
        cell: (row, index) => index + 1,
        center: true,
        width: "80px",
        grow: 0,
      },
      {
        name: "Actions",
        cell: (row, index) => (
          <div className="flex space-x-2 justify-center">
            <button
              className="text-red-500 hover:text-red-700 p-1 rounded"
              onClick={() => handleDeleteRow(index)}
              title="Delete row"
            >
              <IconTrash size={16} />
            </button>
          </div>
        ),
        center: true,
        width: "100px",
        grow: 0,
      },
    ];

    // Generate columns for each editable column with dynamic width
    editableColumns.forEach((col) => {
      const columnData = editData.map(
        (row) => row[col.name] ?? row[col.originalName] ?? ""
      );
      const constraints = getColumnConstraints(col.name);
      const width = calculateColumnWidth(
        columnData,
        col.name,
        constraints.min,
        constraints.max
      );

      const columnDef = {
        name: (
          <div
            className="flex flex-col w-full relative"
            style={{ minWidth: `${constraints.min}px` }}
          >
            {col.isEditing ? (
              <div className="w-full bg-blue-50 rounded border border-blue-400 relative">
                <input
                  type="text"
                  className="w-full px-2 py-1 text-sm bg-blue-50 text-gray-800 rounded"
                  value={col.name}
                  onChange={(e) =>
                    handleColumnNameChange(
                      editableColumns.findIndex(
                        (c) => c.originalName === col.originalName
                      ),
                      e.target.value
                    )
                  }
                  onBlur={() =>
                    toggleColumnNameEditing(
                      editableColumns.findIndex(
                        (c) => c.originalName === col.originalName
                      )
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      toggleColumnNameEditing(
                        editableColumns.findIndex(
                          (c) => c.originalName === col.originalName
                        )
                      );
                    }
                  }}
                  autoFocus
                />
                <button
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-800 p-1 rounded-full bg-white bg-opacity-80"
                  onClick={() =>
                    toggleColumnNameEditing(
                      editableColumns.findIndex(
                        (c) => c.originalName === col.originalName
                      )
                    )
                  }
                  title="Save column name"
                >
                  <IconCheck size={14} />
                </button>
              </div>
            ) : (
              <div className="w-full flex flex-col items-start">
                <div
                  className="truncate max-w-full w-full text-left cursor-pointer hover:text-blue-300 mb-1"
                  title={col.name}
                  onClick={() =>
                    toggleColumnNameEditing(
                      editableColumns.findIndex(
                        (c) => c.originalName === col.originalName
                      )
                    )
                  }
                >
                  {col.name}
                </div>

                <div className="flex space-x-1 mt-1">
                  <button
                    className="text-blue-300 hover:text-blue-100 bg-blue-700 p-1 rounded-md"
                    onClick={() =>
                      toggleColumnNameEditing(
                        editableColumns.findIndex(
                          (c) => c.originalName === col.originalName
                        )
                      )
                    }
                    title="Edit column name"
                  >
                    <IconEdit size={14} />
                  </button>
                  <button
                    className="text-red-300 hover:text-red-100 bg-blue-700 p-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => confirmDeleteColumn(col.name)}
                    title="Delete column"
                    disabled={["status"].includes(
                      normalizeHeaderName(col.name)
                    )}
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ),
        selector: (row) => row[col.name] ?? row[col.originalName] ?? "",
        center: false,
        width: `${width}px`,
        minWidth: `${constraints.min}px`,
        maxWidth: `${constraints.max}px`,
        grow: constraints.fixed ? 0 : 1,
        style: {
          justifyContent: "flex-start",
        },
        cell: (row, index) => {
          const normalizedColName = normalizeHeaderName(col.name);
          const value =
            row[normalizedColName] ??
            row[col.name] ??
            row[col.originalName] ??
            "";

          console.log(`Rendering cell for row ${index}, column ${col.name}:`, {
            normalizedColName,
            value,
            rowData: row,
          }); // Debug log

          if (normalizedColName === "status") {
            return (
              <select
                className="border border-gray-300 rounded px-2 py-1 w-full min-w-0"
                value={value}
                onChange={(e) => {
                  console.log(
                    `Status change for row ${index}:`,
                    e.target.value
                  );
                  handleCellChange(index, normalizedColName, e.target.value);
                }}
              >
                <option value="">Select Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            );
          }

          if (normalizedColName === "tag" || normalizedColName === "tags") {
            return (
              <select
                className="border border-gray-300 rounded px-2 py-1 w-full min-w-0"
                value={value}
                onChange={(e) => {
                  console.log(`Tag change for row ${index}:`, e.target.value);
                  handleCellChange(index, normalizedColName, e.target.value);
                }}
              >
                <option value="">Select Tag</option>
                {tagOptions.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            );
          }

          if (normalizedColName.includes("deadline") && !normalizedColName.includes("note")) {
            return (
              <input
                type="date"
                className="border border-gray-300 rounded px-2 py-1 w-full min-w-0"
                value={getFormattedDateForInput(value)}
                onChange={(e) => {
                  handleCellChange(index, normalizedColName, e.target.value);
                }}
              />
            );
          }

          if (normalizedColName.includes("deadline") && normalizedColName.includes("note")) {
            return (
              <input
                type="text"
                placeholder="e.g. ASAP, Round 1"
                className="border border-gray-300 rounded px-2 py-1 w-full min-w-0"
                value={value}
                onChange={(e) => {
                  handleCellChange(index, normalizedColName, e.target.value);
                }}
              />
            );
          }

          if (normalizedColName.includes("link")) {
            return (
              <input
                type="url"
                placeholder="https://"
                className="border border-gray-300 rounded px-2 py-1 w-full min-w-0"
                value={value}
                onChange={(e) => {
                  console.log(`URL change for row ${index}:`, e.target.value);
                  handleCellChange(index, normalizedColName, e.target.value);
                }}
              />
            );
          }

          if (
            ["remarks", "details", "requirements", "notes"].some((keyword) =>
              normalizedColName.includes(keyword)
            )
          ) {
            return (
              <textarea
                className="border border-gray-300 rounded px-2 py-1 w-full min-w-0 resize-y"
                value={value}
                onChange={(e) => {
                  console.log(
                    `Textarea change for row ${index}:`,
                    e.target.value
                  );
                  handleCellChange(index, normalizedColName, e.target.value);
                }}
                rows={2}
              />
            );
          }

          return (
            <textarea
              className="border border-gray-300 rounded px-2 py-1 w-full min-w-0 resize-y"
              rows={3}
              value={value}
              onChange={(e) => {
                console.log(`Text change for row ${index}:`, e.target.value);
                handleCellChange(index, normalizedColName, e.target.value);
              }}
            />
          );
        },
      };

      editableDataColumns.push(columnDef);
    });

    return editableDataColumns;
  };

  // Save all changes
  const handleSaveAllChanges = async () => {
    try {
      // First check if any column names have been changed
      const columnChanges = editableColumns.filter(
        (col) =>
          normalizeHeaderName(col.name) !==
          normalizeHeaderName(col.originalName)
      );

      // Apply column name changes to data
      let updatedData = [...editData];
      if (columnChanges.length > 0) {
        updatedData = updatedData.map((row) => {
          const newRow = { ...row };
          columnChanges.forEach((col) => {
            // Only rename if the field exists in this row
            if (newRow.hasOwnProperty(col.originalName)) {
              const normalizedNewName = normalizeHeaderName(col.name);
              newRow[normalizedNewName] = newRow[col.originalName];
              if (
                normalizeHeaderName(col.name) !==
                normalizeHeaderName(col.originalName)
              ) {
                delete newRow[col.originalName];
              }
            }
          });
          return newRow;
        });
      }

      // Filter out metadata rows before saving but preserve them for tracking
      const metadataRows = updatedData.filter(
        (row) => row.__columnOrder || row.__deletedColumns
      );
      const cleanData = updatedData.filter(
        (row) => !row.__columnOrder && !row.__deletedColumns
      );

      // Update data state
      setData([...cleanData, ...metadataRows]);

      // Create column order for saving (normalized) - exclude deleted columns
      const deletedColumnsRow = metadataRows.find(
        (row) => row.__deletedColumns
      );
      const deletedColumns = deletedColumnsRow?.__deletedColumns || [];

      const normalizedColumnNames = editableColumns
        .map((col) => normalizeHeaderName(col.name))
        .filter(
          (colName) =>
            !deletedColumns.some(
              (deletedCol) => normalizeHeaderName(deletedCol) === colName
            )
        );

      // Remove duplicates from column order
      const uniqueColumnNames = [...new Set(normalizedColumnNames)];

      const columnOrderRow = {
        __columnOrder: uniqueColumnNames,
      };

      // Update or add column order row
      const existingOrderRowIndex = metadataRows.findIndex(
        (row) => row.__columnOrder
      );
      if (existingOrderRowIndex !== -1) {
        metadataRows[existingOrderRowIndex] = columnOrderRow;
      } else {
        metadataRows.push(columnOrderRow);
      }

      // Save changes to backend - include metadata for tracking
      const finalDataToSave = ensureStatusField([
        ...cleanData,
        ...metadataRows,
      ]);

      if (activeTab === "finalizedUniversity") {
        await postFinalizedData(studentId, finalDataToSave);
      } else {
        await addShortlistingEntry(studentId, finalDataToSave);
      }

      // Use the wrapper function instead of direct call
      handleFetchStudentDetails();
      toast.success("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes. Please try again.");
    }
  };

  const handleSendShortlistingNotification = async () => {
    try {
      await sendShortlistingNotification(studentId);
    } catch (err) {
      console.log("Error sending notification:", err);
    }
  };

  // Add new row
  const handleAddRow = () => {
    if (!isEditMode) {
      toast.error("Please enter edit mode first to add a row.");
      return;
    }

    if (editableColumns.length === 0) {
      toast.error("No columns available. Please add a column first.");
      return;
    }

    const newRow = {
      id: generateUniqueId(),
    };

    // Add empty values for all current editable columns
    editableColumns.forEach((col) => {
      const normalizedName = normalizeHeaderName(col.name);
      if (normalizedName === "status") {
        newRow[normalizedName] = "Pending";
      } else {
        newRow[normalizedName] = "";
      }
    });

    console.log("Adding new row:", newRow);
    setEditData((prevData) => [...(prevData || []), newRow]);
  };

  // Delete row
  const handleDeleteRow = (rowIndex) => {
    if (!isEditMode) {
      toast.error("Please enter edit mode first to delete a row.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this row?")) {
      return;
    }

    const updatedData = [...editData];
    updatedData.splice(rowIndex, 1);
    setEditData(updatedData);
  };

  // Add column modal
  const openAddColumnModal = () => {
    if (!isEditMode) {
      toast.error("Please enter edit mode first to add a column.");
      return;
    }
    setIsAddColumnModalOpen(true);
  };

  // Add new column
  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      toast.error("Please enter a column name.");
      return;
    }

    const normalizedNewName = normalizeHeaderName(newColumnName);

    // Check for duplicates
    const existingNames = editableColumns.map((col) =>
      normalizeHeaderName(col.name)
    );
    if (existingNames.includes(normalizedNewName)) {
      toast.error(
        "Column name already exists. Please choose a different name."
      );
      return;
    }

    // Add to editable columns
    setEditableColumns([
      ...editableColumns,
      {
        name: normalizedNewName,
        originalName: normalizedNewName,
        isEditing: false,
      },
    ]);

    // Add empty value for this column to all rows
    const updatedData = editData.map((row) => ({
      ...row,
      [normalizedNewName]: "",
    }));
    setEditData(updatedData);

    // Reset and close modal
    setNewColumnName("");
    setIsAddColumnModalOpen(false);
    toast.success("Column added successfully!");
  };

  // Delete column confirmation
  const confirmDeleteColumn = (columnName) => {
    if (!isEditMode) {
      toast.error("Please enter edit mode first to delete a column.");
      return;
    }

    const normalizedColumnName = normalizeHeaderName(columnName);
    if (["status"].includes(normalizedColumnName)) {
      toast.error("Cannot delete required columns (status, tag).");
      return;
    }

    setColumnToDelete(columnName);
    setIsDeleteColumnModalOpen(true);
  };

  // Delete column
  const handleDeleteColumn = () => {
    if (!columnToDelete) return;

    try {
      const normalizedColumnToDelete = normalizeHeaderName(columnToDelete);

      // Remove from editable columns
      const updatedColumns = editableColumns.filter(
        (col) => normalizeHeaderName(col.name) !== normalizedColumnToDelete
      );
      setEditableColumns(updatedColumns);

      // Remove this column from all data rows - be more thorough
      const updatedData = editData.map((row) => {
        const newRow = { ...row };

        // Remove all possible variations of the column name
        Object.keys(newRow).forEach((key) => {
          if (normalizeHeaderName(key) === normalizedColumnToDelete) {
            delete newRow[key];
          }
        });

        return newRow;
      });
      setEditData(updatedData);

      // Track deleted columns for persistence
      const deletedColumnsRow = updatedData.find((row) => row.__deletedColumns);
      if (deletedColumnsRow) {
        if (!deletedColumnsRow.__deletedColumns.includes(columnToDelete)) {
          deletedColumnsRow.__deletedColumns.push(columnToDelete);
        }
        if (
          !deletedColumnsRow.__deletedColumns.includes(normalizedColumnToDelete)
        ) {
          deletedColumnsRow.__deletedColumns.push(normalizedColumnToDelete);
        }
      } else {
        // Add deleted columns tracking
        updatedData.push({
          __deletedColumns: [columnToDelete, normalizedColumnToDelete],
        });
      }

      setEditData(updatedData);

      // Close modal
      setColumnToDelete(null);
      setIsDeleteColumnModalOpen(false);
      toast.success("Column deleted successfully!");
    } catch (error) {
      console.error("Error deleting column:", error);
      toast.error("Failed to delete column. Please try again.");
    }
  };

  const handleFinalizeClick = async () => {
    if (selectedStudent.is_finalised) {
      const confirmUnfinalize = window.confirm(
        "Are you sure you want to Unfinalize this University?"
      );
      if (!confirmUnfinalize) return;

      try {
        await finalizeUniversity(selectedStudent.student_id, false, note);
        dispatch(toggleFinalizationStatus(false));
        toast.success("University has been Unfinalized.");

        // Call fetch function directly
        fetchStudentDetails();
      } catch (error) {
        console.error("Error unfinalizing university:", error);
        toast.error("Failed to unfinalize university.");
      }
    } else {
      setIsFinalizeModalOpen(true);
    }
  };

  const handleAssignAndFinalize = async () => {
    if (!selectedManager) {
      toast.error("Please select an Application Manager before finalizing.");
      return;
    }

    try {
      await updateCustomer(selectedStudent.customer_id, {
        assigned_application_manager: selectedManager,
      });

      await finalizeUniversity(selectedStudent.student_id, true, note);
      dispatch(toggleFinalizationStatus(true));

      toast.success(`University finalized successfully!`);
      setIsFinalizeModalOpen(false);

      // Call fetch function directly
      fetchStudentDetails();
    } catch (err) {
      console.error("Error in finalizing university:", err);
      toast.error("Failed to finalize university. Please try again.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  const handleDownloadAsExcel = () => {
    if (data.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    // Get the filtered data (excluding metadata rows)
    const filteredData = data.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    if (filteredData.length === 0) {
      toast.error("No university data to export.");
      return;
    }

    // Get column headers in the same order as displayed in the table
    // Exclude Sr.No., Edit, Delete, and checkbox columns
    const exportColumns = columns
      .filter(
        (col) =>
          !["sr.no.", "edit", "delete", "actions"].includes(
            normalizeHeaderName(col.name)
          ) && typeof col.name === "string" // Exclude checkbox column which has JSX name
      )
      .map((col) => col.name);

    // Remove duplicate columns
    const uniqueExportColumns = [
      ...new Set(exportColumns.map((col) => normalizeHeaderName(col))),
    ];

    // Create formatted data maintaining the same column order
    const formattedData = filteredData.map((row, index) => {
      const formattedRow = {};

      // Add Sr.No. as the first column
      formattedRow["Sr.No."] = index + 1;

      // Add other columns in the same order as the table
      uniqueExportColumns.forEach((columnName) => {
        const value = row[columnName] || "";

        // Handle different data types
        if (typeof value === "string") {
          // Convert URLs to Excel hyperlinks
          if (isValidUrl(value) && !value.startsWith("=HYPERLINK(")) {
            formattedRow[columnName] = {
              f: `HYPERLINK("${value}","${value}")`,
              v: `${value}`,
              l: { Target: value },
            };
          } else {
            formattedRow[columnName] = value;
          }
        } else {
          formattedRow[columnName] = value;
        }
      });

      return formattedRow;
    });

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Set column widths based on content
    const columnWidths = [];

    // Sr.No. column width
    columnWidths.push({ wch: 8 });

    // Calculate width for each column based on header and content
    uniqueExportColumns.forEach((columnName) => {
      const maxLength = Math.max(
        columnName.length,
        ...formattedData.map((row) => String(row[columnName] || "").length)
      );
      columnWidths.push({ wch: Math.min(Math.max(maxLength + 2, 15), 50) });
    });

    worksheet["!cols"] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    const sheetName =
      activeTab === "finalizedUniversity"
        ? "Finalized Universities"
        : "Shortlisted Universities";

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `${sheetName
      .toLowerCase()
      .replace(/\s+/g, "_")}_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename, { bookType: "xlsx" });

    toast.success(`Excel file downloaded successfully!`);
  };

  const handlePostFinalizedData = async () => {
    if (!studentId) {
      toast.error("No valid student ID found!");
      return;
    }

    if (isPosting) return;

    try {
      setIsPosting(true);

      // Filter out metadata rows and ensure each object has a `status` key
      const cleanData = data
        .filter((row) => !row.__columnOrder && !row.__deletedColumns)
        .map((item) => ({
          ...item,
          status: item.status || "Pending",
        }));

      const response = await putFinalizedData(studentId, cleanData);
      console.log("Response from API:", response);
      toast.success("Finalized university data successfully submitted!");
      fetchStudentDetails();
    } catch (error) {
      console.error("Error submitting finalized university data:", error);
      toast.error("Failed to submit finalized data. Please try again.");
    } finally {
      setIsPosting(false);
    }
  };

  const handleImportSelectedData = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select rows to import.");
      return;
    }

    try {
      setIsPosting(true);

      const selectedData = data
        .filter((row) => !row.__columnOrder && !row.__deletedColumns)
        .filter((row) => selectedRows.includes(row.id));

      if (selectedData.length === 0) {
        toast.error("No matching data found for the selected rows.");
        setIsPosting(false);
        return;
      }

      // Build normalized & mapped payload
      const records = selectedData.map((row) =>
        buildRecordForBackend(
          row,
          selectedStudent,
          assignedApplicationManagerId
        )
      );

      // Send in { records: [...] } envelope
      await ImportData({ records });

      toast.success("Selected data successfully imported!");
      fetchStudentDetails();
    } catch (error) {
      console.error("Error importing selected data:", error);
      toast.error("Failed to import data. Please try again.");
    } finally {
      setIsPosting(false);
    }
  };
  const toggleFullScreenMode = () => {
    dispatch(toggleFullScreen());
  };

  // Generate editable columns for the DataTable
  const getEditableDataColumns = () => {
    if (!isEditMode || !editData) {
      return columns;
    }

    return getEditableDataColumnsWithDynamicWidth(editData, editableColumns);
  };

  // Enhanced useEffect for data loading with dynamic columns
  useEffect(() => {
    if (!selectedStudent) {
      console.warn("selectedStudent is not available");
      return;
    }

    const rawDataset =
      activeTab === "finalizedUniversity"
        ? selectedStudent?.is_finalized_excel ?? []
        : selectedStudent?.shortlisting ?? [];

    const dataset = rawDataset.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    // Always use column order from shortlisting
    const shortlistingOrderRow = selectedStudent?.shortlisting?.find(
      (row) => row.__columnOrder
    );

    const deletedColumnRow = rawDataset.find((row) => row.__deletedColumns);
    const deletedColumns = deletedColumnRow?.__deletedColumns ?? [];

    // 🔽 PASTE THE NEW ORDERING LOGIC HERE
    const orderRowFromActive = rawDataset.find((row) => row?.__columnOrder);
    const orderRowFromShortlisting =
      selectedStudent?.shortlisting?.find((row) => row?.__columnOrder) || null;

    // Base ordered headers = active tab's order, else shortlisting's order, else []
    let orderedHeaders = (
      orderRowFromActive?.__columnOrder ||
      orderRowFromShortlisting?.__columnOrder ||
      []
    ).map((key) => normalizeHeaderName(key));

    // Respect deletions
    orderedHeaders = orderedHeaders.filter(
      (key) =>
        !["edit", "delete", "sr.no"].includes(key) &&
        !deletedColumns.map((c) => normalizeHeaderName(c)).includes(key)
    );

    // Collect all keys visible in the dataset (normalized)
    const allKeys = Array.from(
      new Set(
        dataset.flatMap((row) =>
          row ? Object.keys(row).map((k) => normalizeHeaderName(k)) : []
        )
      )
    ).filter((k) => !["edit", "delete", "sr.no"].includes(k));

    // Append any missing keys (that aren’t deleted) to preserve visibility
    allKeys.forEach((key) => {
      if (
        !orderedHeaders.includes(key) &&
        !["__columnorder", "__deletedcolumns"].includes(key) &&
        !deletedColumns.map((c) => normalizeHeaderName(c)).includes(key)
      ) {
        orderedHeaders.push(key);
      }
    });

    // Dedup
    orderedHeaders = [...new Set(orderedHeaders)];

    // Ensure "status" exists for finalized view if not deleted
    if (
      activeTab === "finalizedUniversity" &&
      !orderedHeaders.includes("status") &&
      !deletedColumns.map((c) => normalizeHeaderName(c)).includes("status")
    ) {
      orderedHeaders.unshift("status");
    }

    setColumnOrder(orderedHeaders);

    // // Collect all keys from current dataset and normalize them
    // const allKeys = Array.from(
    //   new Set(
    //     dataset.flatMap((row) =>
    //       row ? Object.keys(row).map((key) => normalizeHeaderName(key)) : []
    //     )
    //   )
    // ).filter((key) => !["edit", "delete", "sr.no"].includes(key));

    // // Final ordered headers = shortlisting order + any extras from dataset (all normalized)
    // let orderedHeaders = (shortlistingOrderRow?.__columnOrder || [])
    //   .map((key) => normalizeHeaderName(key))
    //   .filter(
    //     (key) =>
    //       !["edit", "delete", "sr.no"].includes(key) &&
    //       !deletedColumns.map((col) => normalizeHeaderName(col)).includes(key)
    //   );

    // // Ensure missing keys are included but exclude deleted ones
    // allKeys.forEach((key) => {
    //   const normalizedKey = normalizeHeaderName(key);
    //   if (
    //     !orderedHeaders.includes(normalizedKey) &&
    //     !["__columnOrder", "__deletedColumns"].includes(key) &&
    //     !deletedColumns
    //       .map((col) => normalizeHeaderName(col))
    //       .includes(normalizedKey)
    //   ) {
    //     orderedHeaders.push(normalizedKey);
    //   }
    // });

    // // Remove duplicates from ordered headers
    // orderedHeaders = [...new Set(orderedHeaders)];

    // // Ensure "status" is always included if not deleted
    // if (
    //   activeTab === "finalizedUniversity" &&
    //   !orderedHeaders.includes("status") &&
    //   !deletedColumns.map((col) => normalizeHeaderName(col)).includes("status")
    // ) {
    //   orderedHeaders.unshift("status");
    // }

    // setColumnOrder(orderedHeaders);

    const newData = dataset.map((row) => {
      const idValue =
        row.id && !isNaN(Number(row.id)) ? Number(row.id) : generateUniqueId();

      // Normalize all keys in the row
      const normalizedRow = {};
      Object.keys(row).forEach((key) => {
        const normalizedKey = normalizeHeaderName(key);
        normalizedRow[normalizedKey] = row[key];
      });

      const rowWithId = {
        ...normalizedRow,
        id: idValue,
      };

      orderedHeaders.forEach((key) => {
        if (
          !(key in rowWithId) ||
          rowWithId[key] === null ||
          rowWithId[key] === undefined
        ) {
          if (key === "status") {
            rowWithId[key] = "Pending"; // Default value
          } else {
            rowWithId[key] = "";
          }
        }
        // else if (typeof rowWithId[key] !== "string" && key !== "id") {
        //   rowWithId[key] = String(rowWithId[key]);
        // }
      });

      return rowWithId;
    });

    // Add any metadata rows
    const metadataRows = rawDataset.filter(
      (row) => row.__columnOrder || row.__deletedColumns
    );
    const completeData = [...newData, ...metadataRows];

    // Use the new dynamic column building function
    const dynamicColumns = buildDynamicColumns(
      completeData,
      orderedHeaders,
      deletedColumns
    );

    setColumns(dynamicColumns);
    setData(completeData);

    // If we're in edit mode, prepare editable columns after data is set
    if (isEditMode) {
      setTimeout(() => {
        prepareEditableColumns();
      }, 0);
    }
  }, [activeTab, selectedStudent, isEditMode]);

  const handleShortlistingNotification = async () => {
    try {
      setIsNotifying(true);
      await sendShortlistingNotification(studentId);
      toast.success("Shortlisting notification sent successfully!");
    } catch (err) {
      console.log("Error sending shortlisting notification:", err);
    } finally {
      setIsNotifying(false);
    }
  };

  const handleFinalizedNotification = async () => {
    try {
      setIsNotifying(true);
      await sendFinalizedNotification(studentId);
      toast.success("Finalized notification sent successfully!");
    } catch (err) {
      console.log("Error sending finalized notification:", err);
    } finally {
      setIsNotifying(false);
    }
  };

  // Updated custom styles for better responsive behavior
  const customStyles = {
    table: {
      style: {
        borderCollapse: "separate",
        borderSpacing: 0,
        width: "100%",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        borderRadius: "8px",
        overflow: "hidden",
        tableLayout: "auto", // Allow dynamic sizing
      },
    },
    headRow: {
      style: {
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        minHeight: "56px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#2563eb",
        color: "white",
        textAlign: "center",
        padding: "12px 8px",
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        minHeight: "48px",
        "& input": {
          backgroundColor: "#f9fafb",
          color: "#1f2937",
          borderRadius: "4px",
          padding: "2px 6px",
          border: "1px solid #3b82f6",
        },
      },
    },
    cells: {
      style: {
        whiteSpace: "normal",
        wordWrap: "break-word",
        textAlign: "center",
        padding: "8px",
        borderRight: "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        fontSize: "14px",
        transition: "background-color 0.2s",
        minHeight: "48px",
        overflow: "hidden",
      },
    },
    rows: {
      style: {
        backgroundColor: "white",
        minHeight: "48px",
        "&:nth-of-type(odd)": {
          backgroundColor: "#f9fafb",
        },
        "&:hover": {
          backgroundColor: "#f3f4f6",
          cursor: "pointer",
        },
        transition: "background-color 0.2s",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f3f4f6",
        cursor: "pointer",
      },
    },
    pagination: {
      style: {
        borderTop: "none",
        marginTop: "10px",
        borderRadius: "0 0 8px 8px",
      },
      pageButtonsStyle: {
        color: "#2563eb",
        fill: "#2563eb",
        "&:disabled": {
          color: "#c3c5c8",
          fill: "#c3c5c8",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "#eff6ff",
        },
        "&:focus": {
          outline: "none",
        },
      },
    },
  };

  // Button styling classes
  const primaryButtonClass =
    "bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 flex items-center gap-2";
  const secondaryButtonClass =
    "bg-gray-100 text-gray-700 px-3 py-2 rounded shadow hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center gap-2";
  const dangerButtonClass =
    "bg-red-600 text-white px-3 py-2 rounded shadow hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 flex items-center gap-2";
  const successButtonClass =
    "bg-green-600 text-white px-3 py-2 rounded shadow hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-300 flex items-center gap-2";
  const warningButtonClass =
    "bg-yellow-500 text-white px-3 py-2 rounded shadow hover:bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-300 flex items-center gap-2";
  const editModeButtonClass =
    "bg-purple-600 text-white px-3 py-2 rounded shadow hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 flex items-center gap-2";

  useEffect(() => {
    const fetchApplicationData = async () => {
      try {
        const response = await getApplicationRecords();
        setApplicationData(response);
      } catch (error) {
        console.error("Failed to fetch application data:", error);
      }
    };

    fetchApplicationData();
  }, []);

  const appliedUniversityNames = applicationData.map((app) =>
    normalizeHeaderName(app.university || "")
  );

  return (
    <>
      <div
        className={`bg-white border border-blue-100 shadow-lg rounded-lg ${
          isTableFullScreen
            ? "fixed top-0 left-0 w-screen h-screen z-[100] overflow-hidden"
            : "relative w-full h-[660px] md:h-[620px] overflow-hidden"
        }`}
      >
        <div className="absolute left-0 w-full h-full px-6 py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {activeTab === "finalizedUniversity"
                ? "Finalized Universities"
                : "Shortlisted Universities"}
            </h2>
            <div className="flex flex-wrap gap-3">
              {/* Always show Edit Mode toggle button */}
              <button
                className={
                  isEditMode ? successButtonClass : editModeButtonClass
                }
                onClick={toggleEditMode}
              >
                {isEditMode ? (
                  <>
                    <IconDeviceFloppy size={18} />
                    Save Changes
                  </>
                ) : (
                  <>
                    <IconPencil size={18} />
                    Edit Mode
                  </>
                )}
              </button>

              {/* Conditional buttons based on edit mode */}
              {isEditMode && (
                <>
                  <button className={primaryButtonClass} onClick={handleAddRow}>
                    <IconPlus size={18} />
                    Add Row
                  </button>
                  <button
                    className={secondaryButtonClass}
                    onClick={openAddColumnModal}
                  >
                    <IconPlus size={18} />
                    Add Column
                  </button>
                </>
              )}
              {!isEditMode && (
                <button
                  className={warningButtonClass}
                  onClick={() => {
                    activeTab === "finalizedUniversity"
                      ? handleFinalizedNotification()
                      : handleShortlistingNotification();
                  }}
                  disabled={isNotifying}
                >
                  {isNotifying ? (
                    <>
                      <span className="animate-spin">⟳</span>
                      Notifying...
                    </>
                  ) : (
                    <>
                      <IconCheck size={18} />
                      Notify Students
                    </>
                  )}
                </button>
              )}
              {/* Non-edit mode buttons */}
              {!isEditMode && (
                <>
                  {activeTab === "finalizedUniversity" && (
                    <>
                      <button
                        className={warningButtonClass}
                        onClick={handleImportSelectedData}
                        disabled={isPosting}
                      >
                        {isPosting ? (
                          <>
                            <span className="animate-spin">⟳</span>
                            Importing...
                          </>
                        ) : (
                          <>
                            <IconFileImport size={18} />
                            Import Data In Table
                          </>
                        )}
                      </button>

                      <button
                        className={primaryButtonClass}
                        onClick={handlePostFinalizedData}
                        disabled={isPosting}
                      >
                        {isPosting ? (
                          <>
                            <span className="animate-spin">⟳</span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <IconPlus size={18} />
                            Show Data To Students
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {activeTab !== "finalizedUniversity" && (
                    <button
                      className={`${
                        selectedStudent.is_finalised
                          ? dangerButtonClass
                          : successButtonClass
                      }`}
                      onClick={handleFinalizeClick}
                    >
                      {selectedStudent.is_finalised ? (
                        <>
                          <IconMinimize size={18} />
                          Unfinalize University
                        </>
                      ) : (
                        <>
                          <IconCheck size={18} />
                          Finalize University
                        </>
                      )}
                    </button>
                  )}

                  <button
                    className={successButtonClass}
                    onClick={handleDownloadAsExcel}
                  >
                    <IconDownload size={18} />
                    Download Excel
                  </button>
                </>
              )}

              <button
                className={`${
                  isTableFullScreen ? warningButtonClass : secondaryButtonClass
                }`}
                onClick={toggleFullScreenMode}
              >
                <IconMinimize size={18} />
                {isTableFullScreen ? "Exit Full Screen" : "Full Screen"}
              </button>
            </div>
          </div>
          <div
            className={`${
              isTableFullScreen ? "h-[calc(100%-120px)]" : "h-[520px]"
            } transition-all duration-300 ease-in-out transform bg-white rounded-lg overflow-hidden`}
          >
            <div className="w-full h-full overflow-auto">
              <DataTable
                columns={
                  isEditMode
                    ? getEditableDataColumns()
                    : [
                        // Conditionally add the checkbox column for finalizedUniversity
                        ...(activeTab === "finalizedUniversity" && !isEditMode
                          ? [
                              {
                                name: (
                                  <div className="flex justify-center">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          const filteredData = data.filter(
                                            (row) =>
                                              !row.__columnOrder &&
                                              !row.__deletedColumns
                                          );
                                          setSelectedRows(
                                            filteredData.map((row) => row.id)
                                          );
                                        } else {
                                          setSelectedRows([]);
                                        }
                                      }}
                                      checked={
                                        selectedRows.length > 0 &&
                                        selectedRows.length ===
                                          data.filter(
                                            (row) =>
                                              !row.__columnOrder &&
                                              !row.__deletedColumns
                                          ).length
                                      }
                                    />
                                  </div>
                                ),
                                cell: (row) => (
                                  <div className="flex justify-center">
                                    <div className="relative">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 hover:border-blue-400 transition-all duration-200"
                                        checked={selectedRows.includes(row.id)}
                                        onChange={() =>
                                          handleRowSelection(row.id)
                                        }
                                        title="Select this university"
                                      />
                                    </div>
                                  </div>
                                ),
                                width: "60px",
                                style: {
                                  justifyContent: "center",
                                },
                              },
                            ]
                          : []),

                        ...columns,
                      ]
                }
                data={
                  isEditMode
                    ? (editData || []).filter(
                        (row) => !row.__columnOrder && !row.__deletedColumns
                      )
                    : data.filter(
                        (row) => !row.__columnOrder && !row.__deletedColumns
                      )
                }
                pagination={!isTableFullScreen}
                fixedHeader={true}
                fixedHeaderScrollHeight={
                  isTableFullScreen ? "calc(100vh - 180px)" : "460px"
                }
                responsive
                striped
                highlightOnHover
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                paginationDefaultPage={currentPage}
                customStyles={customStyles}
                noDataComponent={
                  <div className="p-10 text-center text-gray-500">
                    No university data found.{" "}
                    {isEditMode
                      ? "Click 'Add Row' to get started."
                      : "Enable edit mode to add data."}
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Column Modal */}
      {isAddColumnModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[500]">
          <div className="bg-white p-6 shadow-xl rounded-lg w-full md:w-1/3 max-w-md">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Add New Column
              </h2>
              <button
                onClick={() => {
                  setIsAddColumnModalOpen(false);
                  setNewColumnName("");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Column Name
                </label>
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter column name (will be converted to lowercase)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newColumnName.trim()) {
                      handleAddColumn();
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Note: Column names will be automatically converted to
                  lowercase
                </p>
              </div>

              <div className="flex justify-end pt-4 border-t space-x-3">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={() => {
                    setIsAddColumnModalOpen(false);
                    setNewColumnName("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={handleAddColumn}
                  disabled={!newColumnName.trim()}
                >
                  Add Column
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Column Confirmation Modal */}
      {isDeleteColumnModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[500]">
          <div className="bg-white p-6 shadow-xl rounded-lg w-full md:w-1/3 max-w-md">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Confirm Delete
              </h2>
              <button
                onClick={() => {
                  setIsDeleteColumnModalOpen(false);
                  setColumnToDelete(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <p className="mb-4">
              Are you sure you want to delete the column "{columnToDelete}"?
              This action cannot be undone and will remove all data in this
              column.
            </p>

            <div className="flex justify-end pt-4 border-t space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => {
                  setIsDeleteColumnModalOpen(false);
                  setColumnToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                onClick={handleDeleteColumn}
              >
                Delete Column
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Modal */}
      {isFinalizeModalOpen && !selectedStudent.is_finalised && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white p-6 shadow-xl rounded-lg w-full md:w-1/3 max-w-md">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Assign Application Manager
              </h2>
              <button
                onClick={() => setIsFinalizeModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <p className="mb-4 text-gray-600">
              Select an Application Manager before finalizing the university
              list:
            </p>

            <select
              className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
            >
              <option value="">-- Select Manager --</option>
              {admins.map((admin, index) => (
                <option key={index} value={admin.admin_id}>
                  {admin.username}
                </option>
              ))}
            </select>

            <div className="mb-6">
              <label
                htmlFor="assignment-notes"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Assignment Notes
              </label>
              <textarea
                id="assignment-notes"
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                rows="3"
                placeholder="Add any special instructions or notes for the assigned manager..."
                value={note}
                onChange={handleChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Provide context or special requirements for this
                assignment
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => setIsFinalizeModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleAssignAndFinalize}
              >
                Assign & Finalize
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UniversityTable;
